import React, { useMemo } from 'react';
import BreakdownTable, {
  type BreakdownCell,
  type BreakdownCellContext,
} from '@/components/ui/breakdown-tables/BreakdownTable';
import { SupplierPayment } from '@/api/rebates/types';
import {
  RebatePoint,
  REBATE_SUPPLIER_PAYMENT_TYPE_LABELS,
  RebateSupplierPaymentType,
} from '@erp/shared-types';
import { formatPaymentDate } from 'src/utils/dateUtils';
import { formatCurrency } from '@erp/shared-utils';
import { deriveCumulativePosition } from 'src/utils/rebateUtils';
import { Badge } from '@/components/ui/badge';
import { CheckCircleIcon, WarningCircleIcon } from '@phosphor-icons/react';
import { aggregateDebtorsReportCellValues } from '@/utils/rebates/debtorsReportAggregation';

interface DebtorsReportAllocationsTableProps {
  suppliers: { id: string; name: string }[];
  paymentsData: SupplierPayment[];
  rebateData: RebatePoint[];
}

type PaymentStatus =
  | 'matched'
  | 'slightly_underpaid'
  | 'underpaid'
  | 'slightly_overpaid'
  | 'overpaid'
  | 'not_paid';

function calculatePaymentStatus(
  forecasted: number,
  paid: number,
): PaymentStatus {
  if (paid === 0) return 'not_paid';
  if (forecasted === 0) return paid === 0 ? 'matched' : 'overpaid';

  const variancePercent = ((paid - forecasted) / forecasted) * 100;

  // Check if matched (within 1%)
  if (Math.abs(variancePercent) < 1) return 'matched';

  // Underpaid (paid < forecasted)
  if (variancePercent < 0) {
    return Math.abs(variancePercent) < 5 ? 'slightly_underpaid' : 'underpaid';
  }

  // Overpaid (paid > forecasted)
  return variancePercent < 5 ? 'slightly_overpaid' : 'overpaid';
}

function getStatusConfig(status: PaymentStatus) {
  switch (status) {
    case 'matched':
      return {
        label: 'Matched',
        color: 'bg-green-100 text-green-800',
        icon: CheckCircleIcon,
      };
    case 'slightly_underpaid':
      return {
        label: 'Slightly Underpaid',
        color: 'bg-yellow-100 text-yellow-800',
        icon: WarningCircleIcon,
      };
    case 'underpaid':
      return {
        label: 'Underpaid',
        color: 'bg-red-100 text-red-800',
        icon: WarningCircleIcon,
      };
    case 'slightly_overpaid':
      return {
        label: 'Slightly Overpaid',
        color: 'bg-blue-100 text-blue-800',
        icon: WarningCircleIcon,
      };
    case 'overpaid':
      return {
        label: 'Overpaid',
        color: 'bg-purple-100 text-purple-800',
        icon: WarningCircleIcon,
      };
    case 'not_paid':
      return {
        label: 'Not Paid',
        color: 'bg-gray-100 text-gray-800',
        icon: WarningCircleIcon,
      };
  }
}

const columns = [
  'Status',
  'Difference',
  'Rebate YTD',
  'Gross Amount',
  'Tax Withheld',
  'Net Allocated',
  'Type',
  'Memo',
];

interface DivisionInfo {
  id: string;
  name: string;
}

interface AllocationWithPayment {
  allocation: {
    id: string;
    allocatedAmount: number;
    division: { id: string; name: string } | null;
    createdAt: string;
  };
  payment: SupplierPayment;
}

const DebtorsReportAllocationsTable: React.FC<
  DebtorsReportAllocationsTableProps
> = ({ suppliers, paymentsData, rebateData }) => {
  // Extract unique divisions from rebate data breakdowns
  const divisions = useMemo(() => {
    const divisionMap = new Map<string, DivisionInfo>();

    const cumulativePosition = deriveCumulativePosition(rebateData || []);

    if (cumulativePosition) {
      cumulativePosition.breakdowns.forEach((breakdown) => {
        if (breakdown.divisionId && breakdown.divisionName) {
          if (!divisionMap.has(breakdown.divisionId)) {
            divisionMap.set(breakdown.divisionId, {
              id: breakdown.divisionId,
              name: breakdown.divisionName,
            });
          }
        }
      });
    }

    return Array.from(divisionMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [rebateData]);

  // Owed YTD per division - sum all point-in-time rebate data to get cumulative
  const owedByDivision = useMemo(() => {
    const map = new Map<string, number>();

    const cumulativePosition = deriveCumulativePosition(rebateData || []);

    if (!cumulativePosition) return map;

    // Sum breakdowns by division to get YTD totals
    cumulativePosition.breakdowns.forEach((breakdown) => {
      if (breakdown.divisionId) {
        const current = map.get(breakdown.divisionId) || 0;
        map.set(breakdown.divisionId, current + breakdown.rebateAmount);
      }
    });

    return map;
  }, [rebateData]);

  // Group allocations by division and compute paid totals
  const {
    allocationsByDivision,
    paidByDivision,
    divisionStatuses,
    allocationsWithPayments,
  } = useMemo(() => {
    const byDivision = new Map<string, AllocationWithPayment[]>();
    const totals = new Map<string, number>();
    const statuses = new Map<string, PaymentStatus>();
    const allAllocations: AllocationWithPayment[] = [];

    // Extract all allocations from payments (filter out payments with no allocations)
    paymentsData?.forEach((payment) => {
      if (!payment.allocations || payment.allocations.length === 0) {
        return; // Exclude unallocated payments per requirement 1b
      }

      payment.allocations.forEach((allocation) => {
        if (allocation.division) {
          allAllocations.push({ allocation, payment });
        }
      });
    });

    // Group allocations by division
    allAllocations.forEach(({ allocation, payment }) => {
      const divisionId = allocation.division?.id;
      if (!divisionId) return;

      if (!byDivision.has(divisionId)) {
        byDivision.set(divisionId, []);
      }
      byDivision.get(divisionId)!.push({ allocation, payment });

      // Sum allocated amounts by division
      totals.set(
        divisionId,
        (totals.get(divisionId) || 0) + allocation.allocatedAmount,
      );
    });

    // Calculate status for each division
    divisions.forEach((division) => {
      const forecasted = owedByDivision.get(division.id) || 0;
      const paid = totals.get(division.id) || 0;
      statuses.set(division.id, calculatePaymentStatus(forecasted, paid));
    });

    return {
      allocationsByDivision: byDivision,
      paidByDivision: totals,
      divisionStatuses: statuses,
      allocationsWithPayments: allAllocations,
    };
  }, [paymentsData, divisions, owedByDivision]);

  // Transform data into flat BreakdownCell[] format
  const data = useMemo(() => {
    const cells: BreakdownCell[] = [];

    divisions.forEach((division) => {
      const forecasted = owedByDivision.get(division.id) || 0;
      const paid = paidByDivision.get(division.id) || 0;
      const difference = paid - forecasted;
      const status = divisionStatuses.get(division.id) || 'not_paid';

      const allocations = (allocationsByDivision.get(division.id) || []).sort(
        (a, b) => {
          // Sort by payment date (newest first), then by allocation date
          const dateA = new Date(a.payment.date).getTime();
          const dateB = new Date(b.payment.date).getTime();
          if (dateB !== dateA) return dateB - dateA;
          return (
            new Date(b.allocation.createdAt).getTime() -
            new Date(a.allocation.createdAt).getTime()
          );
        },
      );

      // Calculate totals for gross and tax (proportional to allocation ratio)
      const totalGross = allocations.reduce((sum, { allocation, payment }) => {
        if (!payment.grossAmount || payment.amount === 0) return sum;
        const ratio = allocation.allocatedAmount / payment.amount;
        return sum + payment.grossAmount * ratio;
      }, 0);

      const totalTax = allocations.reduce((sum, { allocation, payment }) => {
        if (!payment.taxAmount || payment.amount === 0) return sum;
        const ratio = allocation.allocatedAmount / payment.amount;
        return sum + payment.taxAmount * ratio;
      }, 0);

      // Division-level cells (subRow = null)
      columns.forEach((column) => {
        let value: number | string | null = null;

        if (column === 'Status') {
          value = status; // Store status as string, will be rendered specially
        } else if (column === 'Rebate YTD') {
          value = forecasted;
        } else if (column === 'Difference') {
          value = difference;
        } else if (column === 'Gross Amount') {
          value = totalGross > 0 ? totalGross : null;
        } else if (column === 'Tax Withheld') {
          value = totalTax > 0 ? totalTax : null;
        } else if (column === 'Net Allocated') {
          value = paid;
        } else if (column === 'Type' || column === 'Memo') {
          value = null; // Only shown for allocation rows
        }

        cells.push({
          column,
          subColumn: null,
          row: division.name,
          subRow: null,
          value,
          metadata: {
            divisionId: division.id,
            isGroupLevel: true,
            status,
          },
        });
      });

      // Allocation-level cells (subRow = agreement name and date)
      allocations.forEach(({ allocation, payment }) => {
        const agreementName =
          payment.rebateAgreement?.name || 'Unknown Agreement';
        const allocationLabel = `${agreementName}, ${formatPaymentDate(
          payment.date,
        )}`;
        const typeValue = String(payment.type);
        const paymentType =
          typeValue && typeValue !== '' && typeValue !== '0'
            ? REBATE_SUPPLIER_PAYMENT_TYPE_LABELS[
                typeValue as RebateSupplierPaymentType
              ] || typeValue
            : '...';

        // Calculate proportional amounts for this allocation
        const allocationRatio =
          payment.amount > 0 ? allocation.allocatedAmount / payment.amount : 0;
        const proportionalGross =
          payment.grossAmount && payment.grossAmount > 0
            ? payment.grossAmount * allocationRatio
            : null;
        const proportionalTax =
          payment.taxAmount && payment.taxAmount > 0
            ? payment.taxAmount * allocationRatio
            : null;

        columns.forEach((column) => {
          let value: number | string | null = null;

          if (
            column === 'Status' ||
            column === 'Rebate YTD' ||
            column === 'Difference'
          ) {
            value = null; // Only shown at division level
          } else if (column === 'Gross Amount') {
            value = proportionalGross;
          } else if (column === 'Tax Withheld') {
            value = proportionalTax;
          } else if (column === 'Net Allocated') {
            value = allocation.allocatedAmount;
          } else if (column === 'Type') {
            value = paymentType;
          } else if (column === 'Memo') {
            value = payment.memo ?? '';
          }

          cells.push({
            column,
            subColumn: null,
            row: division.name,
            subRow: allocationLabel,
            subRowKey: allocation.id, // Use allocation ID as unique key
            value,
            metadata: {
              divisionId: division.id,
              allocationId: allocation.id,
              paymentId: payment.id,
              isGroupLevel: false,
              taxRate: payment.taxRate,
            },
          });
        });
      });
    });

    return cells;
  }, [
    divisions,
    owedByDivision,
    paidByDivision,
    divisionStatuses,
    allocationsByDivision,
  ]);

  // Custom cell renderer
  const renderCell = (context: BreakdownCellContext) => {
    const { cell } = context;
    const metadata = cell.metadata as
      | {
          divisionId?: string;
          allocationId?: string;
          paymentId?: string;
          isGroupLevel?: boolean;
          status?: PaymentStatus;
          taxRate?: number | null;
        }
      | undefined;

    const column = cell.column;
    const value = cell.value;
    const isGroupLevel = metadata?.isGroupLevel ?? false;

    // Handle Status column with badge (only at division level)
    if (column === 'Status' && isGroupLevel) {
      const status = metadata?.status;
      if (status) {
        const config = getStatusConfig(status);
        const Icon = config.icon;
        return (
          <Badge
            className={`${config.color} text-xs cursor-default hover:bg-inherit`}
          >
            <Icon className="h-3 w-3 mr-1" />
            {config.label}
          </Badge>
        );
      }
      return <span className="text-xs text-muted-foreground">-</span>;
    }

    // Handle Status column for allocation rows (should be null)
    if (column === 'Status' && !isGroupLevel) {
      return <span className="text-xs text-muted-foreground">-</span>;
    }

    // Handle numeric values
    if (typeof value === 'number') {
      // For Difference column, use reconciliation colors
      if (column === 'Difference') {
        if (value === 0) {
          return <span className="text-xs text-muted-foreground">-</span>;
        }

        const colorClass =
          value > 0
            ? 'text-purple-600' // Overpaid
            : 'text-red-600'; // Underpaid

        return (
          <span className={`text-xs font-medium ${colorClass}`}>
            {value > 0 ? '+' : ''}
            {formatCurrency(value)}
          </span>
        );
      }

      // Color coding for Tax Withheld
      if (column === 'Tax Withheld') {
        const taxRate = metadata?.taxRate;
        const displayValue = value === 0 ? 0 : value;

        if (displayValue === 0) {
          return <span className="text-xs text-muted-foreground">-</span>;
        }

        return (
          <div className="text-xs">
            <div className="font-medium text-red-600">
              -{formatCurrency(displayValue)}
            </div>
            {taxRate !== null && taxRate !== undefined && (
              <div className="text-[10px] text-gray-500">({taxRate}%)</div>
            )}
          </div>
        );
      }

      // Color coding for Net Allocated
      if (column === 'Net Allocated') {
        if (value === 0) {
          return <span className="text-xs text-muted-foreground">-</span>;
        }

        return (
          <span className="text-xs font-medium text-green-700">
            {formatCurrency(value)}
          </span>
        );
      }

      // For other numeric columns (Forecasted, Gross Amount), use neutral color
      if (column === 'Rebate YTD' || column === 'Gross Amount') {
        if (value === 0) {
          return <span className="text-xs text-muted-foreground">-</span>;
        }
      }

      return (
        <span className="text-xs font-medium text-foreground">
          {formatCurrency(value)}
        </span>
      );
    }

    // Handle string values (Type, Memo)
    if (typeof value === 'string') {
      return (
        <span className="text-xs font-medium text-foreground">
          {value || '-'}
        </span>
      );
    }

    // Handle null values
    return <span className="text-xs text-muted-foreground">-</span>;
  };

  return (
    <BreakdownTable
      data={data}
      rowLabel="Division"
      subRowLabel="Allocation Date"
      columnLabel="Metric"
      title="Payment Allocation by Division"
      renderCell={renderCell}
      aggregateCellValues={aggregateDebtorsReportCellValues}
      showYDim1Total={true}
      maxHeight="calc(100vh - 200px)"
      xDimOrder={columns}
      hideVerticalLines={true}
      columnHeaderAlign="left"
    />
  );
};

export default DebtorsReportAllocationsTable;
